import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) {
    throw new Error(body?.message || 'Enterprise memory request failed');
  }
  return body.data;
}

export type MemoryCandidate = {
  id: string;
  companyId: string;
  ruleKey: string;
  memoryType: string;
  domain: string;
  title: string;
  proposedSummary: string;
  proposedDetail?: string | null;
  status: string;
  subjectType?: string | null;
  subjectId?: string | null;
  subjectLabel?: string | null;
  sourceEventNumber?: string | null;
  evidence?: unknown[];
  createdAt: string;
};

export type EnterpriseMemoryRow = {
  id: string;
  title: string;
  summary: string;
  status: string;
  memoryType: string;
  domain: string;
  trustScore: number;
  subjectLabel?: string | null;
  updatedAt: string;
};

export type RetrievedMemoryRow = {
  memory: EnterpriseMemoryRow & { id: string; title: string; summary: string; trustScore: number };
  relevanceScore: number;
  reasons?: string[];
  diagnostics?: Record<string, number>;
};

export const enterpriseMemoryService = {
  listCandidates: (params?: { status?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return unwrap<MemoryCandidate[]>(api.get(`/enterprise-memory/candidates${qs ? `?${qs}` : ''}`));
  },
  getCandidate: (id: string) => unwrap<MemoryCandidate>(api.get(`/enterprise-memory/candidates/${id}`)),
  createCandidate: (payload: Record<string, unknown>) =>
    unwrap<{ candidate: MemoryCandidate; duplicates: unknown[] }>(
      api.post('/enterprise-memory/candidates', payload)
    ),
  patchCandidate: (id: string, payload: Record<string, unknown>) =>
    unwrap<MemoryCandidate>(api.patch(`/enterprise-memory/candidates/${id}`, payload)),
  promoteCandidate: (id: string, payload: Record<string, unknown>) =>
    unwrap<EnterpriseMemoryRow>(api.post(`/enterprise-memory/candidates/${id}/promote`, payload)),
  listMemories: (params?: { status?: string; domain?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.domain) q.set('domain', params.domain);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return unwrap<EnterpriseMemoryRow[]>(api.get(`/enterprise-memory${qs ? `?${qs}` : ''}`));
  },
  getMemory: (id: string) => unwrap<Record<string, unknown>>(api.get(`/enterprise-memory/${id}`)),
  publish: (id: string, notes?: string) =>
    unwrap<EnterpriseMemoryRow>(api.post(`/enterprise-memory/${id}/publish`, { notes })),
  submitReview: (id: string) => unwrap<EnterpriseMemoryRow>(api.post(`/enterprise-memory/${id}/submit-review`, {})),
  retire: (id: string) => unwrap<EnterpriseMemoryRow>(api.post(`/enterprise-memory/${id}/retire`, {})),
  feedback: (id: string, rating: string, comment?: string) =>
    unwrap<{ id: string }>(api.post(`/enterprise-memory/${id}/feedback`, { rating, comment })),
  search: (q: string) => unwrap<RetrievedMemoryRow[]>(api.get(`/enterprise-memory/search?q=${encodeURIComponent(q)}`)),
  retrieve: (body: Record<string, unknown>) =>
    unwrap<RetrievedMemoryRow[]>(api.post('/enterprise-memory/retrieve', body)),
  health: () => unwrap<Record<string, unknown>>(api.get('/enterprise-memory/health')),
};
